<script setup lang="ts">
const configuration = inject('configuration') as Ref<Config | null>

const apps = inject('apps') as Ref<App[] | null>

const socialMediaList = inject('socialMedias') as Ref<SocialMedia[] | null>

const { t, locale } = useI18n()

const footer = (await useFetchTheFooter()).value?.[locale.value]

const sustainabilityURL = computed(
  () => `https://nachhaltigkeit.deutschebahn.com/${locale}`
)

const runtimeConfig = useRuntimeConfig()
const client = runtimeConfig.public.client
const clientMap = runtimeConfig.public.clientMapping as ClientMapping

const currentYear = new Date().getFullYear()

const getTheAppText =
  client === 'flinkster'
    ? t('dbc-footer-get-the-app-flinkster')
    : t('dbc-footer-get-the-app')
</script>

<template>
  <footer class="dbc-gl-footer">
    <div class="dbc-footer-top">
      <section class="dbc-gl-section">
        <div class="dbc-footer-grid dbc-footer-brand">
          <div class="dbc-footer-logo">
            <NuxtLink :to="getPagePath['home'][locale]">
              <UtilityImage
                :src="configuration?.logo?.url"
                :alt="`${clientMap[client]} Logo`"
                :width="configuration?.logo?.width"
                :height="configuration?.logo?.height"
                :logo="true"
              />
            </NuxtLink>
          </div>
          <div class="dbc-footer-get-the-app">
            <h3 class="dbc-gt-h3">
              {{ getTheAppText }}
            </h3>
            <div class="dbc-footer-app-container">
              <UtilityAppsBadge v-for="app in apps" :key="app.id" :app="app" />
            </div>
            <div class="dbc-footer-qr-container">
              <img
                class="dbc-footer-qr"
                src="/qrcode.svg"
                :alt="`QR Code ${getTheAppText}`"
              />
              <p class="dbc-footer-qr-info">
                {{
                  `${
                    client === 'flinkster'
                      ? $t('dbc-footer-qr-info-flinkster')
                      : $t('dbc-footer-qr-info')
                  }`
                }}
              </p>
            </div>
          </div>
          <div class="dbc-footer-faq">
            <h3 class="dbc-gt-h3">
              {{ `${$t('dbc-footer-any-questions')}` }}
            </h3>
            <NuxtLink
              class="dbc-gc-btn dbc-gc-btn--ghost dbc-footer-faq-button"
              activeClass="dbc-c-menu--footer--active"
              :to="footer?.faq_link.url"
              :title="footer?.faq_link.text"
            >
              {{ footer?.faq_link.text }}
            </NuxtLink>
          </div>
        </div>
      </section>
      <section
        class="dbc-gl-section dbc-footer-section-links dbc-footer-section-links--grid"
      >
        <ul v-for="sectionLink in footer?.section_links">
          <li>
            <component
              :is="
                sectionLink.sub_links && sectionLink.sub_links.length > 0
                  ? 'h4'
                  : 'div'
              "
              class="dbc-gt-h4"
            >
              <NuxtLink
                :to="sectionLink.url"
                :target="resolveLinkTarget(sectionLink.url)"
                class="dbc-text-color-black"
              >
                {{ sectionLink.text }}
              </NuxtLink>
            </component>
          </li>
          <li v-if="sectionLink.sub_links && sectionLink.sub_links.length > 0">
            <ul>
              <li v-for="sub in sectionLink.sub_links">
                <NuxtLink
                  :to="sub.url"
                  :target="resolveLinkTarget(sub.url)"
                  class="dbc-text-color-black"
                >
                  {{ sub.text }}
                </NuxtLink>
              </li>
            </ul>
          </li>
        </ul>
      </section>
    </div>
    <div class="dbc-footer-bottom">
      <section class="dbc-gl-section dbc-footer-flex">
        <div class="flex">
          <NuxtLink
            v-for="socialMedia in socialMediaList"
            :key="socialMedia.id"
            class="mr-s"
            :to="socialMedia.link"
            :title="`social media link to ${socialMedia.channel}`"
            target="_blank"
          >
            <DbcSvg :name="`dbc-icon-${socialMedia.channel}`" />
          </NuxtLink>
        </div>
        <div>
          <ul class="dbc-footer-flex dbc-footer-legal-links">
            <li v-for="legalLink in footer?.legal_links">
              <NuxtLink
                :to="legalLink.url"
                :target="resolveLinkTarget(legalLink.url)"
                class="dbc-text-color-black"
              >
                {{ legalLink.text }}
              </NuxtLink>
            </li>
            <li>
              <button
                v-if="client !== 'srlg'"
                class="border-none bg-transparent cursor-pointer p-0"
                onclick="window.DB_CMP.showSecondLayer()"
              >
                {{ `${$t('dbc-text-manage-consent')}` }}
              </button>
            </li>
            <li>
              <NuxtLink :to="`/${locale}/sitemap`" class="dbc-text-color-black"
                >Sitemap</NuxtLink
              >
            </li>
          </ul>
        </div>
        <div class="dbc-footer-copy">
          <NuxtLink
            v-if="configuration?.client !== 'flinkster'"
            :to="sustainabilityURL"
            target="_blank"
          >
            <img
              class="dbc-footer-sustainability"
              :src="`/this-is-green-${locale}.svg`"
              :alt="$t('dbc-sustainability-alt-text')"
            />
          </NuxtLink>
          <p>&copy;&nbsp;Deutsche&nbsp;Bahn&nbsp;AG&nbsp;{{ currentYear }}</p>
        </div>
      </section>
    </div>
  </footer>
</template>
